/**
 * Bilingual display: "Purchase Order (أمر شراء)".
 *
 * Implemented as an i18next post-processor, so EVERY `t()` call in the app is
 * bilingual when the mode is on — no component writes Arabic beside English
 * itself, and none has to know the mode exists.
 *
 * Whole strings, never words: the secondary text is the translation of the
 * same KEY, so "Purchase Order" is paired with "أمر شراء", not with
 * "شراء" + "أمر". A key with no translation in the secondary language — or
 * whose translation is identical, like "PDF" — is shown once, not twice.
 *
 * The secondary text is wrapped in Unicode isolates (FSI ... PDI), so Arabic
 * inside an English sentence (and the reverse) keeps its own direction and
 * cannot reorder the punctuation around it.
 *
 * A call can opt out with `t(key, { bilingual: false })` — for text that is
 * not read by a person in the moment, such as an export file name, or for a
 * label interpolated into another translated sentence (which would otherwise
 * be paired twice).
 */

export const POST_PROCESSOR = 'bilingual';

const FSI = '⁨';
const PDI = '⁩';

/** "Settings" + "الإعدادات" -> "Settings (الإعدادات)". */
export function composeBilingual(primary, secondary) {
  if (!secondary || secondary === primary) return primary;
  return `${primary} (${FSI}${secondary}${PDI})`;
}

/**
 * @param {() => { enabled: boolean, secondary: string | null }} getState
 *   read on every call, so switching the mode needs no re-initialisation.
 */
export function createBilingualPostProcessor(getState) {
  return {
    type: 'postProcessor',
    name: POST_PROCESSOR,
    process(value, key, options, translator) {
      const { enabled, secondary } = getState();
      if (!enabled || !secondary) return value;
      if (options?.bilingual === false) return value;
      if (typeof value !== 'string' || !value.trim()) return value;
      if ((options?.lng ?? translator.language) === secondary) return value;

      const keys = Array.isArray(key) ? key : [key];
      const exists = translator.exists(keys, { ...options, lng: secondary, fallbackLng: false });
      if (!exists) return value;

      const other = translator.translate(keys, {
        ...options,
        lng: secondary,
        // The secondary text is final: it must not be post-processed (and so
        // paired) a second time.
        postProcess: [],
        bilingual: false,
      });
      return composeBilingual(value, typeof other === 'string' ? other : null);
    },
  };
}
